import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from './Navbar';

// Helper function to get effective roles based on role hierarchy
const getEffectiveRoles = (roles) => {
  // Define role hierarchy (same as in RouteWithRoleCheck)
  const roleHierarchy = {
    'Superadmin': ['Superadmin', 'Admin', 'Editor', 'Viewer'],
    'Admin': ['Admin', 'Editor', 'Viewer'],
    'Editor': ['Editor', 'Viewer'],
    'Viewer': ['Viewer']
  };
  
  // Get all effective roles
  return roles.reduce((allRoles, role) => {
    if (roleHierarchy[role]) {
      return [...allRoles, ...roleHierarchy[role]];
    }
    return [...allRoles, role];
  }, []);
};

/**
 * Component to display when a user doesn't have the required role for a subdomain
 * 
 * @param {Object} props
 * @param {string} props.subdomain - The subdomain the user tried to access
 * @param {string[]} props.userRoles - The roles the user has for this subdomain
 * @param {string[]} props.requiredRoles - The roles required to access the requested resource
 * @param {Object} props.school - The school object if found
 */
const AccessDeniedScreen = ({ subdomain, userRoles = [], requiredRoles = [], school }) => {
  const { t } = useTranslation();
  
  // Get effective roles based on hierarchy
  const effectiveRoles = getEffectiveRoles(userRoles);
  
  // Log detailed information for debugging
  console.log('[AccessDeniedScreen] Rendering with:', { 
    subdomain, 
    userRoles, 
    effectiveRoles,
    requiredRoles, 
    school: school ? `${school.name} (ID: ${school.id})` : 'Not found' 
  });
  
  return (
    <>
      <Navbar />
      <div className="max-w-4xl mx-auto p-6 bg-white shadow-md rounded-lg mt-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900">
            {t('accessDenied.title', 'Access Denied')}
          </h1>
          
          <p className="mt-2 text-gray-600">
            {t('accessDenied.subtitle', 'You don\'t have permission to access this page')}
          </p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-md mb-6">
          <h2 className="font-medium text-gray-700 mb-2">
            {t('accessDenied.details', 'Access Details')}:
          </h2>
          
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">{t('accessDenied.school', 'School')}:</span> {
                school
                  ? `${school.name} (${subdomain})`
                  : subdomain || t('accessDenied.unknownSchool', 'Unknown school')
              }
            </p>
            
            <p>
              <span className="font-medium">{t('accessDenied.yourRoles', 'Your roles')}:</span> {
                userRoles.length > 0 
                  ? userRoles.join(', ') 
                  : t('accessDenied.noRoles', 'No roles assigned')
              }
            </p>
            
            <p>
              <span className="font-medium">{t('accessDenied.effectiveRoles', 'Your effective roles')}:</span> {
                effectiveRoles.length > 0 
                  ? [...new Set(effectiveRoles)].join(', ') // Remove duplicates
                  : t('accessDenied.noRoles', 'No roles assigned')
              }
            </p>
            
            <p>
              <span className="font-medium">{t('accessDenied.requiredRoles', 'Required roles')}:</span> {
                requiredRoles.join(', ')
              }
            </p>
          </div>
        </div>
        
        {!school && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  {t('accessDenied.schoolNotFound', "The school you're trying to access wasn't found in your list of schools.")}
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="text-center space-y-4">
          <p className="text-gray-600">
            {t('accessDenied.contactAdmin', 'Please contact your school administrator if you need access to this page.')}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/dashboard" 
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('accessDenied.goToDashboard', 'Go to Dashboard')}
            </Link>
            
            {subdomain && (
              <Link 
                to={`/${subdomain}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t('accessDenied.viewPublicSite', 'View Public Site')}
              </Link>
            )}
            
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('accessDenied.refreshPage', 'Refresh Page')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccessDeniedScreen; 